import { useEffect, useState } from 'react'
import { KeyRound, Plus, Trash2, Copy, Check } from 'lucide-react'
import {
  issueRecorderToken,
  listRecorderTokens,
  revokeRecorderToken,
  type RecorderTokenRow
} from '../lib/recorderTokens'

interface Props {
  managerId: string
}

/**
 * Settings sub-section for issuing and revoking Recorder Helper tokens.
 * Renders inside `pages/Settings.tsx` next to Profile and Password.
 *
 * The plaintext token is shown exactly once at issue time — only the
 * sha256 hash is persisted server-side. Managers paste the plaintext
 * into the Recorder Helper Mac app on first launch.
 */
export function RecorderTokensSection({ managerId }: Props) {
  const [rows, setRows] = useState<RecorderTokenRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [issuedPlaintext, setIssuedPlaintext] = useState<string | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)

  async function refresh() {
    setLoading(true)
    try {
      setRows(await listRecorderTokens(managerId))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId])

  async function onIssue() {
    setBusy(true)
    setError(null)
    try {
      const { plaintext } = await issueRecorderToken({
        managerId,
        label: newLabel.trim() || null
      })
      setIssuedPlaintext(plaintext)
      setNewLabel('')
      void refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function onRevoke(id: string) {
    if (
      !confirm(
        'このトークンを失効させますか？該当デバイスは再サインインが必要になります。'
      )
    )
      return
    try {
      await revokeRecorderToken(id)
      void refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function copyPlaintext() {
    if (!issuedPlaintext) return
    void navigator.clipboard.writeText(issuedPlaintext)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-4 h-4 text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-700">
          Recorder Helper トークン
        </h2>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Mac の LogMane Recorder アプリにサインインするためのトークンです。
        Mac 1 台ごとに 1 つ発行してください。
      </p>

      {/* Issue form */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="ラベル（例: MacBook Pro）"
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          disabled={busy}
        />
        <button
          type="button"
          onClick={onIssue}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          発行
        </button>
      </div>

      {issuedPlaintext && (
        <div className="mt-3 mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
          <p className="text-xs text-amber-800 font-semibold mb-2">
            ⚠ このトークンは今だけ表示されます。Recorder Helper
            に貼り付けたら閉じてください。
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-2 py-1.5 font-mono break-all">
              {issuedPlaintext}
            </code>
            <button
              type="button"
              onClick={copyPlaintext}
              className="shrink-0 px-2 py-1.5 border border-amber-300 rounded text-xs text-amber-800 hover:bg-amber-100 flex items-center gap-1"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" /> コピー済み
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" /> コピー
                </>
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setIssuedPlaintext(null)}
            className="mt-2 text-[11px] text-amber-700 underline"
          >
            閉じる
          </button>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {/* List */}
      <div className="border border-gray-100 rounded-lg">
        {loading ? (
          <div className="px-3 py-4 text-sm text-gray-400">読み込み中…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-400">
            まだトークンはありません。上から発行してください。
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r) => (
              <li
                key={r.id}
                className="px-3 py-2.5 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-800 font-medium truncate">
                    {r.label ?? '(無題)'}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    発行 {new Date(r.created_at).toLocaleString('ja-JP')} ·{' '}
                    {r.last_used_at
                      ? `最終使用 ${new Date(r.last_used_at).toLocaleString('ja-JP')}`
                      : '未使用'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRevoke(r.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="失効"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
